// src/pages/Enrollments/EnrollmentList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import Pager from '../../components/common/Pager';
import { PlusIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../supabaseClient';
import { useEnrollmentsPage } from '../../hooks/useEnrollmentsPage';

// Adjust these to match your existing options if they differ
const STATUS_OPTIONS = ['All', 'Active', 'Completed', 'Withdrawn'];

function statusBadgeClass(status) {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800';
    case 'Completed':
      return 'bg-blue-100 text-blue-800';
    case 'Withdrawn':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export default function EnrollmentList() {
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [intakeFilter, setIntakeFilter] = useState('All');

  // Debounce search so we don’t refetch on every keystroke (prevents input blur/flicker)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Pagination (server-side)
  const [pageSize, setPageSize] = useState(20); // default 20
  const [currentPage, setCurrentPage] = useState(1);

  // Load intakes for dropdown (simple list)
  const [intakes, setIntakes] = useState([]);
  const [intakesLoading, setIntakesLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIntakesLoading(true);
        const { data, error } = await supabase
          .from('intakes')
          .select('id, name, start_date')
          .order('start_date', { ascending: false })
          .order('name', { ascending: true });
        if (error) throw error;
        if (!cancelled) setIntakes(data || []);
      } catch (e) {
        console.error('Failed to load intakes', e?.message || e);
        if (!cancelled) setIntakes([]);
      } finally {
        if (!cancelled) setIntakesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Map UI filters to hook params
  const statusParam = statusFilter === 'All' ? 'all' : statusFilter;
  const intakeParam = intakeFilter === 'All' ? 'all' : String(intakeFilter);

  // Server-side data
  const {
    rows,
    count,
    isLoading,
    error,
    softDeleteEnrollment,
  } = useEnrollmentsPage({
    page: currentPage,
    pageSize,
    search: debouncedSearch,
    status: statusParam,
    intakeId: intakeParam,
  });

  // Reset to page 1 when filters (debounced) change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusParam, intakeParam, pageSize]);

  // Clamp page if total shrinks
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((count || 0) / (pageSize || 1)));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [count, pageSize, currentPage]);

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm('Soft-delete this enrollment?')) return;
    try {
      await softDeleteEnrollment(id);
      alert('Enrollment deleted.');
      // count refresh + clamping handled by effects
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Delete failed.');
    }
  };

  const title = useMemo(() => 'Enrollments Management', []);
  const isTyping = searchTerm.trim() !== debouncedSearch;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-xl font-semibold">{title}</h1>
        <Link to="/enrollments/new" className="sm:ml-auto">
          <Button>
            <PlusIcon className="h-5 w-5 mr-1" />
            Add New Enrollment
          </Button>
        </Link>
      </div>

      {/* Error banner (non-blocking) */}
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {/* Filters (stay mounted to preserve input focus) */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <span className="text-xs text-gray-500">
                {isTyping ? 'Typing…' : isLoading ? 'Searching…' : ''}
              </span>
            </div>
            <input
              type="text"
              id="search"
              placeholder="Search by Student Name, NRIC, or Subject..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === 'All' ? 'All Statuses' : s}
                </option>
              ))}
            </select>
          </div>

          {/* Intake */}
          <div>
            <label htmlFor="intake" className="block text-sm font-medium text-gray-700 mb-1">
              Intake
            </label>
            <select
              id="intake"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={intakeFilter}
              onChange={(e) => setIntakeFilter(e.target.value)}
            >
              <option value="All">All Intakes</option>
              {intakes.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
            {intakesLoading && <div className="text-xs text-gray-400 mt-1">Loading intakes…</div>}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {count === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {isLoading && !isTyping
              ? 'Loading enrollments…'
              : `No enrollments${
                  debouncedSearch || statusParam !== 'all' || intakeParam !== 'all'
                    ? ' match your filters'
                    : ' found'
                }`}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto relative">
              {/* Subtle top pulse while fetching, without unmounting anything */}
              {isLoading && !isTyping && (
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent animate-pulse" />
              )}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intake</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enroll Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{r.student?.full_name || '—'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {r.subject?.code || '—'}{r.subject?.name ? ` — ${r.subject.name}` : ''}
                        </div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{r.intake?.name || '—'}</div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {r.enrollment_date
                            ? new Date(r.enrollment_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : '—'}
                        </div>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadgeClass(r.status)}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/enrollments/${r.id}/edit`}>
                            <Button variant="outline" size="sm">Edit</Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(r.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer — shared Pager (uses DB `count`) */}
            <Pager
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalRows={count}
            />
          </>
        )}
      </div>
    </div>
  );
}
